import { content, type Language } from '../config/siteContent'
import type { PortfolioProject } from '../config/site'
import { ArrowIcon } from './icons'

type PortfolioCardProps = {
  language: Language
  project: PortfolioProject
  compact?: boolean
  index?: number
}

export default function PortfolioCard({ language, project, compact = false, index = 0 }: PortfolioCardProps) {
  const copy = content[language].portfolio
  const projectCopy = copy.projects[project.id]
  const roselune = project.id.startsWith('roselune')
  const visibleImages = project.images.slice(0, compact ? 1 : 3)
  const galleryLabel = language === 'el' ? `Εικόνες έργου: ${project.title}` : `Project images: ${project.title}`
  const imageAlt = (index: number) => language === 'el'
    ? `${project.title} — ${index === 0 ? 'κεντρική παρουσίαση' : `λεπτομέρεια έργου ${index + 1}`}`
    : `${project.title} — ${index === 0 ? 'featured presentation' : `project detail ${index + 1}`}`

  const media = (
    <div className="portfolio-card__visual">
      {visibleImages.length > 0 ? (
        <div className={`portfolio-gallery ${compact ? 'portfolio-gallery--compact' : ''}`} role="group" aria-label={galleryLabel}>
          {visibleImages.map((image, index) => (
            <figure className={`portfolio-gallery__item ${index === 0 ? 'portfolio-gallery__item--primary' : ''}`} key={image.src}>
              <img
                src={image.src}
                width={image.width}
                height={image.height}
                style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
                alt={imageAlt(index)}
                loading="lazy"
                decoding="async"
              />
            </figure>
          ))}
        </div>
      ) : (
        <div className="portfolio-placeholder" role="img" aria-label={`${project.title} — branded project placeholder`}>
          <span>{project.id === 'wedbeas' ? 'WB' : project.id === 'neoflex' ? 'NH' : 'RE'}</span>
          <small>{projectCopy.category}</small>
        </div>
      )}
    </div>
  )

  if (compact && project.liveUrl) {
    return (
      <a className={`portfolio-card portfolio-card--compact ${roselune ? 'portfolio-card--roselune' : ''}`} href={project.liveUrl} target="_blank" rel="noreferrer" aria-label={`${copy.viewProjectLabel}: ${project.title}`}>
        {media}
        <div className="portfolio-card__content">
          <div className="portfolio-card__meta"><p className="portfolio-card__category">{projectCopy.category}</p><span className="portfolio-card__index">0{index + 1}</span></div>
          <h3>{project.title}</h3>
          <p className="portfolio-card__result">{projectCopy.result}</p>
          <span className="button button--text"><span>{content[language].common.viewProject}</span><ArrowIcon className="button__icon" /></span>
        </div>
      </a>
    )
  }

  return (
    <article className={`portfolio-card ${roselune ? 'portfolio-card--roselune' : ''}`} id={project.id}>
      {media}
      <div className="portfolio-card__content">
        <div className="portfolio-card__meta"><p className="portfolio-card__category">{projectCopy.category}</p><span className="portfolio-card__index">0{index + 1}</span></div>
        <h2>{project.title}</h2>
        <p className="portfolio-card__role"><strong>{copy.roleLabel}:</strong> {projectCopy.role}</p>
        <p>{projectCopy.description}</p>
        <p className="portfolio-card__result"><strong>{copy.resultLabel}:</strong> {projectCopy.result}</p>
        <ul className="portfolio-meta-list" aria-label={copy.skillsLabel}>{project.technologies.map((technology) => <li key={technology}>{technology}</li>)}</ul>
        {project.liveUrl && (
          <a className="button button--text" href={project.liveUrl} target="_blank" rel="noreferrer">
            <span>{content[language].common.viewProject}</span><ArrowIcon className="button__icon" />
          </a>
        )}
      </div>
    </article>
  )
}
